/*
 * "Add your pets?" (GUARDRAILS G35; mpgadmin G139, D93; CMS G24).
 *
 * A signed-in client with no pets at this salon may copy the pets they added
 * at another salon. The CMS asks the API with the contact this sign-in
 * proved, from its own session; the app sends nothing but the keys the
 * client ticked. The offers name no salon and carry no ids, only a key.
 *
 * Until the CMS and the API carry the routes, or when anything fails, the
 * answer is "no offers" and the booking site behaves exactly as before.
 */
import { CMS_BASE_URL } from '../config/api.config';
import { getCMSJWT } from './auth.service';
import { storageService } from './storage.service';

export interface PetOffer {
  key: string;
  name: string;
  type: 'dog' | 'cat';
  breedName: string | null;
}

/** Which clients put the offer off on this device ("Not now"). Personal: cleared at sign-out (G1). */
export const PET_COPY_DISMISSED_KEY = 'pet_copy_dismissed';

const KEY_SHAPE = /^[0-9a-f]{1,64}$/;

/** The CMS's answer, kept to what the sheet shows; anything else is no offer. The API's PetType: 1 cat, 2 dog. */
export const readOffers = (raw: unknown): PetOffer[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap(o => {
    if (!o || typeof o !== 'object') return [];
    const { key, name, petType, breedName } = o as Record<string, unknown>;
    if (typeof key !== 'string' || !KEY_SHAPE.test(key) || typeof name !== 'string' || !name.trim()) return [];
    if (petType !== 1 && petType !== 2) return [];
    return [{
      key,
      name: name.trim(),
      type: petType === 1 ? 'cat' : 'dog',
      breedName: typeof breedName === 'string' && breedName.trim() ? breedName.trim() : null
    } as PetOffer];
  });
};

/** "Dog · Cavoodle", "Cat". */
export const offerLine = (o: PetOffer): string =>
  [o.type === 'cat' ? 'Cat' : 'Dog', o.breedName].filter(Boolean).join(' · ');

/** "Add 1 pet", "Add 2 pets". */
export const addLabel = (n: number): string => `Add ${n} ${n === 1 ? 'pet' : 'pets'}`;

const dismissedIds = (): string[] => {
  try {
    const list = JSON.parse(storageService.getItem(PET_COPY_DISMISSED_KEY) || '[]');
    return Array.isArray(list) ? list.map(String) : [];
  } catch {
    return [];
  }
};

export const petCopyDismissed = (clientId: number | null): boolean =>
  clientId != null && dismissedIds().includes(String(clientId));

export const dismissPetCopy = (clientId: number | null): void => {
  if (clientId == null) return;
  try {
    const ids = dismissedIds();
    if (!ids.includes(String(clientId))) storageService.setItem(PET_COPY_DISMISSED_KEY, JSON.stringify([...ids, String(clientId)]));
  } catch {
    /* storage refused: it asks again next visit */
  }
};

/** One ask per sign-in per page load. */
let asked: { jwt: string; offers: Promise<PetOffer[]> } | null = null;

export const fetchPetOffers = (): Promise<PetOffer[]> => {
  const jwt = getCMSJWT();
  if (!jwt) return Promise.resolve([]);
  if (asked?.jwt === jwt) return asked.offers;
  const offers = fetch(`${CMS_BASE_URL}/api/pet/elsewhere`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
    body: '{}'
  })
    .then(res => (res.ok ? res.json() : []))
    .then(readOffers)
    .catch(() => [] as PetOffer[]);
  asked = { jwt, offers };
  return offers;
};

/** Copies the ticked pets into this salon's record; the count copied. Throws when the copy failed. */
export const copyPets = async (keys: string[]): Promise<number> => {
  const jwt = getCMSJWT();
  if (!jwt) throw new Error('Not signed in');
  const res = await fetch(`${CMS_BASE_URL}/api/pet/elsewhere/copy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ keys })
  });
  if (!res.ok) throw new Error(`Copy failed (${res.status})`);
  const body = await res.json().catch(() => ({}));
  // The offers are spent either way: copied pets aren't offered again.
  asked = null;
  return typeof body?.copied === 'number' ? body.copied : 0;
};

/** Forget the asked offers (sign-out, tests). */
export const forgetPetOffers = (): void => {
  asked = null;
};
